import { fetchMfhBuilding } from '@/api/mfh';
import { getStrippedCCSRoutes } from '@condo/domain';
import type { RouteRecordRaw } from 'vue-router';

import {
    mfhRamEmbedPropsForAccountingRoute,
    mfhRamEmbedPropsForAddendaRoute,
    mfhRamEmbedPropsForMfhDetailRoute,
    mfhRamEmbedPropsForCapexEeCalculatorRoute,
    mfhRamEmbedPropsForConstructionPlanRoute,
    mfhRamEmbedPropsForContactsRoute,
    mfhRamEmbedPropsForContractsRoute,
    mfhRamEmbedPropsForCostControllingRoute,
    mfhRamEmbedPropsForPlacementRoute,
    mfhRamEmbedPropsForEstatesRoute,
    mfhRamEmbedPropsForInvestmentCaseDetailRoute,
    mfhRamEmbedPropsForInvestmentCaseOverviewRoute,
    mfhRamEmbedPropsForBaseInvestmentCaseConfigureRoute,
    mfhRamEmbedPropsForRenovationProjectsHistoryRoute,
    mfhRamEmbedPropsForRenovationProjectsRoute,
    mfhRamEmbedPropsForRentRollDetailRoute,
    mfhRamEmbedPropsForRentRollOverviewRoute,
    mfhRamEmbedPropsForSignOffEnvelopeAdditionalDocumentRoute,
    mfhRamEmbedPropsForSignOffEnvelopeCreateRoute,
    mfhRamEmbedPropsForSignOffEnvelopeDetailRoute,
    mfhRamEmbedPropsForSignOffEnvelopesOverviewRoute,
    mfhRamEmbedPropsForTaskMilestonesRoute,
    mfhRamEmbedPropsForTransformationElementsIfcRoute,
} from './mfhRamEmbedRouteProps';

const CCSRouteMap = getStrippedCCSRoutes();

export const createMfhRoutes = (): RouteRecordRaw[] => [
    {
        ...CCSRouteMap['one.mfh-by-building.detail'],
        component: () => import('@/views/mfh/OneMfh.vue'),
        async beforeEnter(to, _from, next) {
            const mfhBuilding = await fetchMfhBuilding(+to.params.mfhBuildingId, { select: ['mfhBuildingId', 'mfhId'] });
            next(`/mfhs/${mfhBuilding.mfhId}/detail`);
        },
    },
    {
        ...CCSRouteMap['one.mfh'],
        // Object form on purpose: appending to fullPath puts the segment inside a query/hash
        // (e.g. a leftover #_portal_handoff) so the path never changes → infinite redirect loop.
        redirect: to => ({ path: `${to.path}/${CCSRouteMap['one.mfh.detail'].path}`, query: to.query, hash: to.hash }),
        components: {
            default: () => import('@/views/mfh/OneMfh.vue'),
        },
        props: {
            default: route => ({ mfhId: +route.params.mfhId }),
        },
        children: [
            {
                ...CCSRouteMap['one.mfh.summary'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhSummary.vue'),
                },
            },
            {
                ...CCSRouteMap['one.mfh.detail'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForMfhDetailRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.tasks'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhTasks.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.task-milestones'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForTaskMilestonesRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.project-plan'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhProjectPlanRamPage.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.controlling'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhControlling.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.media'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhMedia.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.contacts'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForContactsRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.documents'],
                components: {
                    default: () => import('@/components/documents/Documents.vue'),
                },
            },
            {
                ...CCSRouteMap['one.mfh.calendar-events'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhCalendarEvents.vue'),
                },
            },
            {
                ...CCSRouteMap['one.mfh.ai-imports'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhAIImports.vue'),
                },
            },
            {
                ...CCSRouteMap['one.mfh.estates'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForEstatesRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.renovation-projects'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForRenovationProjectsRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.renovation-projects-cost-controlling'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForCostControllingRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.renovation-project-tendering-process-details'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/renovation-project/tendering-process/TenderingProcessDetails.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.renovation-project-tendering-process-tenderer-details'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/renovation-project/tendering-process/TendererDetails.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.renovation-project-tendering-process-final-offers-evaluation'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/renovation-project/tendering-process/FinalOffersEvaluation.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.renovation-projects-history'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForRenovationProjectsHistoryRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.addenda'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForAddendaRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.construction-plan'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForConstructionPlanRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.capex-ee-calculator'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForCapexEeCalculatorRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.transformation-contracts'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForContractsRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.timeline'],
                components: {
                    default: () => import('@/components/timeline/EventsTimeline.vue'),
                },
                props: {
                    default: route => ({ ...route.params }),
                },
            },
            {
                ...CCSRouteMap['one.mfh.elements'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForTransformationElementsIfcRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.asset-management.accounting'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForAccountingRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.sourcing.overview'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhSourcingOverview.vue'),
                },
                props: true,
            },
            /*DD Section*/
            {
                path: 'dd',
                name: 'one.mfh.dd',
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhDueDiligence.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.renting-overview'],
                redirect: params => `${params.fullPath}/${CCSRouteMap['one.mfh.renting-overview.external-units'].path}`,
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhRentingOverview.vue'),
                },
                props: true,
                children: [
                    {
                        ...CCSRouteMap['one.mfh.renting-overview.import-estarcore'],
                        components: {
                            default: () => import('@/components/mfh/one-mfh/MFHRentingEstateImportEstarcore.vue'),
                        },
                        props: true,
                    },
                    {
                        ...CCSRouteMap['one.mfh.renting-overview.external-units'],
                        components: {
                            default: () => import('@/components/mfh/one-mfh/MFHRentingExternalUnits.vue'),
                        },
                        props: true,
                    },
                    // SKB-368/hide-link-estarcore
                    // {
                    //     ...CCSRouteMap['one.mfh.renting-overview.estar-core'],
                    //     components: {
                    //         default: () => import('@/components/mfh/one-mfh/MFHEstarCoreEstates.vue'),
                    //     },
                    //     props: true,
                    // },
                ],
            },
            {
                ...CCSRouteMap['one.mfh.dd.rent-roll-versions-overview'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForRentRollOverviewRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.dd.rent-roll-versions-details'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForRentRollDetailRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.dd.placement'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForPlacementRoute,
                },
            },
            /*---END DD Section---*/
            {
                ...CCSRouteMap['one.mfh.purchasing.investment-case-overview'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForInvestmentCaseOverviewRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.purchasing.investment-case-detail'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForInvestmentCaseDetailRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.purchasing.base-investment-case-configure'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForBaseInvestmentCaseConfigureRoute,
                },
            },
            {
                ...CCSRouteMap['one.mfh.subsidy-financing.overview'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhSubsidyFinancingOverview.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.estate-management.overview'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/MfhEstateManagementOverview.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.asset-management-investment-cases.overview'],
                components: {
                    default: () => import('@/components/mfh/one-mfh/mfh-investment-case/MfhAssetManagementInvestmentCaseOverview.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['one.mfh.asset-management-investment-cases.detail'],
                components: {
                    default: () => import('@/views/mfh/MfhInvestmentcaseDetail.vue'),
                },
                props: true,
            },
            {
                ...CCSRouteMap['mfh.sign-off-envelopes.overview'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForSignOffEnvelopesOverviewRoute,
                },
            },
            {
                ...CCSRouteMap['mfh.sign-off-envelopes.estates-overview'],
                components: {
                    default: () => import('@/views/mfh/MfhEstatesSignOffEnvelopesOverview.vue'),
                },
                props: {
                    default: route => ({ mfhId: +route.params.mfhId }),
                },
            },
            {
                ...CCSRouteMap['mfh.sign-off-envelope-create'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForSignOffEnvelopeCreateRoute,
                },
            },
            {
                ...CCSRouteMap['mfh.sign-off-envelope.details'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForSignOffEnvelopeDetailRoute,
                },
            },
            {
                ...CCSRouteMap['mfh.sign-off-envelope.additional-document'],
                components: {
                    default: () => import('@/components/ram/RamEmbed.vue'),
                },
                props: {
                    default: mfhRamEmbedPropsForSignOffEnvelopeAdditionalDocumentRoute,
                },
            },
        ],
    },
];
